import React, { useEffect, useState } from 'react';
import { clients as clientsApi } from '../api';
import { formatDate } from '../utils/format';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';

const emptyForm = { name: '', company: '', whatsapp: '', email: '', gst_number: '', address: '' };

function ClientForm({ value, onChange }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="label">Full Name <span className="text-red-500">*</span></label>
        <input className="input" value={value.name} onChange={e => onChange({ ...value, name: e.target.value })} placeholder="John Doe" />
      </div>
      <div>
        <label className="label">Company / Business</label>
        <input className="input" value={value.company} onChange={e => onChange({ ...value, company: e.target.value })} placeholder="Acme Pvt Ltd" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">WhatsApp Number</label>
          <input className="input" value={value.whatsapp} onChange={e => onChange({ ...value, whatsapp: e.target.value })} placeholder="+91 9876543210" />
        </div>
        <div>
          <label className="label">Email</label>
          <input className="input" type="email" value={value.email} onChange={e => onChange({ ...value, email: e.target.value })} placeholder="john@acme.com" />
        </div>
      </div>
      <div>
        <label className="label">GST Number</label>
        <input className="input" value={value.gst_number} onChange={e => onChange({ ...value, gst_number: e.target.value })} placeholder="27AABCU9603R1ZX" maxLength={15} />
      </div>
      <div>
        <label className="label">Address</label>
        <textarea className="input resize-none min-h-[80px]" value={value.address} onChange={e => onChange({ ...value, address: e.target.value })} placeholder="123 Main St, City, State, ZIP" />
      </div>
    </div>
  );
}

export default function Clients() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => clientsApi.list().then(r => { setData(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditTarget(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (c) => { setEditTarget(c); setForm({ name: c.name, company: c.company || '', whatsapp: c.whatsapp || '', email: c.email || '', gst_number: c.gst_number || '', address: c.address || '' }); setModalOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editTarget) await clientsApi.update(editTarget.id, form);
      else await clientsApi.create(form);
      setModalOpen(false);
      load();
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await clientsApi.delete(deleteTarget.id);
    setDeleteTarget(null);
    load();
  };

  const filtered = data.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.company || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <PageHeader
        title="Clients"
        subtitle={`${data.length} client${data.length !== 1 ? 's' : ''}`}
        actions={
          <button className="btn-primary flex items-center gap-2" onClick={openCreate}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Client
          </button>
        }
      />

      {/* Search */}
      <div className="mb-4">
        <input
          className="input max-w-xs"
          placeholder="Search clients..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-7 h-7 border-4 border-primary-700 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-16 text-gray-400">
          {data.length === 0 ? (
            <>
              <p className="text-lg font-medium mb-2 text-gray-500">No clients yet</p>
              <p className="text-sm mb-4">Add your first client to get started</p>
              <button className="btn-primary mx-auto" onClick={openCreate}>Add Client</button>
            </>
          ) : 'No clients match your search'}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block card overflow-hidden p-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-3 py-3 text-gray-500 font-medium">Name</th>
                  <th className="text-left px-3 py-3 text-gray-500 font-medium">Company</th>
                  <th className="text-left px-3 py-3 text-gray-500 font-medium">WhatsApp</th>
                  <th className="text-left px-3 py-3 text-gray-500 font-medium">Email</th>
                  <th className="text-left px-3 py-3 text-gray-500 font-medium">GST / Tax Number</th>
                  <th className="text-left px-3 py-3 text-gray-500 font-medium">Address</th>
                  <th className="text-left px-3 py-3 text-gray-500 font-medium">Added</th>
                  <th className="px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-3 py-4 font-medium text-gray-900">{c.name}</td>
                    <td className="px-3 py-4 text-gray-600">{c.company || '-'}</td>
                    <td className="px-3 py-4 text-gray-600">
                      {c.whatsapp ? (
                        <a
                          href={`https://wa.me/${c.whatsapp.replace(/\D/g, '')}`}
                          target="_blank" rel="noreferrer"
                          className="text-green-600 hover:underline"
                        >
                          {c.whatsapp}
                        </a>
                      ) : '-'}
                    </td>
                    <td className="px-3 py-4 text-gray-600">{c.email || '-'}</td>
                    <td className="px-3 py-4 text-gray-500 font-mono text-xs">{c.gst_number || '-'}</td>
                    <td className="px-3 py-4 text-gray-600 text-xs" title={c.address || ''}>{c.address || '-'}</td>
                    <td className="px-3 py-4 text-gray-400 text-xs">{formatDate(c.created_at)}</td>
                    <td className="px-3 py-4">
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={() => openEdit(c)} className="text-gray-400 hover:text-primary-600 p-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button onClick={() => setDeleteTarget(c)} className="text-gray-400 hover:text-red-500 p-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {filtered.map(c => (
              <div key={c.id} className="border border-gray-100 rounded-lg p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{c.name}</p>
                    {c.company && <p className="text-sm text-gray-500 truncate">{c.company}</p>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => openEdit(c)} className="text-gray-400 hover:text-primary-600 p-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button onClick={() => setDeleteTarget(c)} className="text-gray-400 hover:text-red-500 p-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
                {c.whatsapp && (
                  <a
                    href={`https://wa.me/${c.whatsapp.replace(/\D/g, '')}`}
                    target="_blank" rel="noreferrer"
                    className="inline-block mt-2 text-sm text-green-600 hover:underline"
                  >
                    {c.whatsapp}
                  </a>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Client' : 'Add New Client'}
      >
        <ClientForm value={form} onChange={setForm} />
        <div className="flex justify-end gap-3 mt-6">
          <button className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={saving || !form.name.trim()}>
            {saving ? 'Saving...' : editTarget ? 'Save Changes' : 'Add Client'}
          </button>
        </div>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Client" size="sm">
        <p className="text-gray-600 text-sm">
          Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This will also delete all associated invoices.
        </p>
        <div className="flex justify-end gap-3 mt-6">
          <button className="btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
          <button className="btn-danger" onClick={confirmDelete}>Delete</button>
        </div>
      </Modal>
    </div>
  );
}
